import FormField from '@components/Common/FormField';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Button, CircularProgress, Grid, Tooltip } from '@mui/material';
import React from 'react';
import './index.scss';

export interface SEOMetadataData {
  title?: string;
  url?: string;
  description?: string;
  keywords?: string;
  metadata: { key: string; value: string }[];
}

export interface SEOMetadataProps {
  /** The metadata object containing all SEO data */
  data: SEOMetadataData;
  /** Callback function to update the metadata */
  onChange: (updatedData: SEOMetadataData) => void;
  /** Whether the component is in edit mode */
  disabled?: boolean;
  /** Custom class name for styling */
  className?: string;
  /** Optional tooltip title for the Metadata URL info icon */
  urlTooltipTitle?: string;
  /**
   * Draft a description with AI. Given, a Generate button appears above the description field.
   *
   * Resolves to the suggested text, or null when generation is switched off or the model's answer
   * failed the backend's length and style checks. The value is put in the field for the operator to
   * review; nothing is saved until they save the record, so a stray click cannot replace copy that
   * somebody wrote by hand.
   */
  onGenerateDescription?: () => Promise<string | null>;
}

const SEOMetadata: React.FC<SEOMetadataProps> = ({
  data,
  onChange,
  disabled = false,
  className = '',
  urlTooltipTitle = '',
  onGenerateDescription
}) => {
  const [generating, setGenerating] = React.useState(false);
  const [generateNote, setGenerateNote] = React.useState('');

  const handleGenerate = async () => {
    if (!onGenerateDescription) return;
    setGenerating(true);
    setGenerateNote('');
    try {
      const suggestion = await onGenerateDescription();
      if (suggestion) {
        handleFieldChange('description', suggestion);
        setGenerateNote('Suggestion added. Review it, then save the record.');
      } else {
        // Not an error state. Generation is off by default, and a rejected answer is the
        // backend refusing to ship something outside 150 to 160 characters.
        setGenerateNote('No suggestion available right now.');
      }
    } catch {
      setGenerateNote('Could not reach the generator.');
    } finally {
      setGenerating(false);
    }
  };
  const handleFieldChange = (field: keyof Omit<SEOMetadataData, 'metadata'>, value: string) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  // Note: handleMetadataChange is intentionally omitted until metadata editor is enabled

  return (
    <div className={`seo-metadata-section ${className}`}>
      <Grid container className="metadata-container" spacing={2}>
        <Grid size={{ xs: 12, md: 12 }}>
          <div className="label-with-tooltip disabled">
            <span className="label-text">Metadata URL</span>
            <Tooltip title={urlTooltipTitle}>
              <InfoOutlinedIcon fontSize="small" className="metadata-info-icon" />
            </Tooltip>
          </div>
          <FormField
            required={false}
            disabled
            label=""
            name="metadata-url"
            value={data.url || ''}
            onChange={(_, value) => handleFieldChange('url', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormField
            required={false}
            disabled={disabled}
            label="Metadata Title"
            name="metadata-title"
            value={data.title || ''}
            onChange={(_, value) => handleFieldChange('title', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormField
            required={false}
            disabled={disabled}
            label="Metadata Keywords"
            name="metadata-keywords"
            value={data.keywords || ''}
            onChange={(_, value) => handleFieldChange('keywords', value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <FormField
            required={false}
            multiline
            disabled={disabled}
            label="Metadata Description"
            name="metadata-description"
            value={data.description || ''}
            onChange={(_, value) => handleFieldChange('description', value)}
            {...(!disabled && { rows: 3 })}
          />
          {/* Only offered when a handler is supplied, so create forms (where the record has no id
              yet, and so nothing to describe) do not show a button that cannot work. */}
          {onGenerateDescription && !disabled && (
            <div className="metadata-generate">
              <Button
                size="small"
                variant="text"
                disabled={generating}
                startIcon={
                  generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />
                }
                onClick={handleGenerate}
              >
                {generating ? 'Generating...' : 'Generate with AI'}
              </Button>
              {generateNote && <span className="metadata-generate-note">{generateNote}</span>}
            </div>
          )}
        </Grid>
        {/* <Grid size={{ xs: 12, md: 12 }}>
          <KeyValuePopup
            data={data.metadata || []}
            disabled={disabled}
            label="Metadata KeyValue"
            setValue={handleMetadataChange}
            title="Metadata KeyValue"
          />
        </Grid> */}
      </Grid>
    </div>
  );
};

export default SEOMetadata;
